'use client';
import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLazyGetCourseByIdQuery } from "../../store/services/university-service";
import MainCard from "../../components/cards/MainCard";
import { Box, CircularProgress, Typography, Alert, Snackbar } from "@mui/material";

export default function TakeScormCourse() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { courseId } = useParams();
    const email = searchParams.get('emailAddress');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);
    const iframeRef = useRef(null);
    const [courseUrl, setCourseUrl] = useState(null);
    const [apiReady, setApiReady] = useState(false);
    const scormApiRef = useRef(null);
    const [getCourseById] = useLazyGetCourseByIdQuery();

    // Set API endpoint for SCORM runtime (loaded in wrapper)
    useEffect(() => {
        // Pass the API URL to the wrapper via window
        window.REACT_APP_RESPONSE_API_URL = process.env.REACT_APP_RESPONSE_API_URL;
        console.log('SCORM API endpoint configured:', process.env.REACT_APP_RESPONSE_API_URL);

        // API is loaded in the wrapper, so we're always ready
        setApiReady(true);
    }, []);

    useEffect(() => {
        if (!email) {
            navigate('/');
            return;
        }

        if (!courseId) {
            setError('No course ID provided');
            setIsLoading(false);
            return;
        }

        const launchCourse = (coursePath, courseType) => {
            // Always use the wrapper to handle path resolution and API access
            // For local URLs, we need to convert them to absolute URLs
            let absoluteCourseUrl;
            if (coursePath.startsWith('http://') || coursePath.startsWith('https://')) {
                // Already absolute
                absoluteCourseUrl = coursePath;
            } else {
                // Convert to absolute URL
                const courseUrlPath = coursePath.startsWith('/') ? coursePath : '/' + coursePath;
                absoluteCourseUrl = window.location.origin + courseUrlPath;
            }

            // Use wrapper for all courses to handle path resolution
            const finalUrl = `/scorm-wrapper.html?` +
                `courseUrl=${encodeURIComponent(absoluteCourseUrl)}&` +
                `emailAddress=${encodeURIComponent(email)}&` +
                `courseIdentifier=${encodeURIComponent(courseId)}` +
                (courseType ? `&courseType=${encodeURIComponent(courseType)}` : '');

            console.log('Loading SCORM course through wrapper:', {
                originalUrl: coursePath,
                absoluteUrl: absoluteCourseUrl,
                wrapperUrl: finalUrl
            });

            setCourseUrl(finalUrl);
            setIsLoading(false);
        };

        // Fallback for courses registered via REACT_APP_SCORM_COURSES instead of the university API
        const loadFromRegistry = () => {
            const scormCoursesConfig = process.env.REACT_APP_SCORM_COURSES;
            if (!scormCoursesConfig) {
                setError('Course not found');
                setIsLoading(false);
                return;
            }

            try {
                const scormCourses = JSON.parse(scormCoursesConfig);
                const course = scormCourses.find(c => c.id === courseId);

                if (!course) {
                    setError('Course not found');
                    setIsLoading(false);
                    return;
                }

                launchCourse(course.url, course.type);
            } catch (err) {
                setError('Error loading SCORM course configuration');
                console.error('Error parsing SCORM courses:', err);
                setIsLoading(false);
            }
        };

        const fetchCourse = async () => {
            try {
                const course = await getCourseById({ courseId: courseId, includes: "" }).unwrap();

                if (course && course.courseContentPath) {
                    launchCourse(course.courseContentPath, course.isXApi ? 'xapi' : 'scorm');
                    return;
                }

                loadFromRegistry();
            } catch (err) {
                console.error('Error fetching course from API, falling back to registry:', err);
                loadFromRegistry();
            }
        };

        fetchCourse();
    }, [courseId, email, navigate, getCourseById]);

    // Function to handle messages from the SCORM content
    useEffect(() => {
        const handleMessage = (event) => {

            console.log('📨 Message received from iframe:', event.data);

            // Handle messages from SCORM content if needed
            if (event.data && event.data.type === 'scorm-complete') {
                console.log('✅ SCORM course completed:', event.data);
                setNotification({
                    type: 'success',
                    message: 'Course completed successfully!'
                });
            }

            // Handle question answered events
            if (event.data && event.data.type === 'scorm-interaction') {
                console.log('❓ Question answered:', event.data.data);
                setNotification({
                    type: 'info',
                    message: `Question answered: ${event.data.data.correct ? 'Correct' : 'Incorrect'}`
                });
            }

            // Handle report status from wrapper
            if (event.data && event.data.type === 'scorm-report-status') {
                console.log('📊 Report status received:', event.data);
                setNotification({
                    type: event.data.status === 'success' ? 'success' : 'error',
                    message: event.data.message
                });
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    // Function to show report status (called from scorm-runtime.js)
    useEffect(() => {
        window.showReportStatus = (status, message) => {

            if (status === 'success') {
                console.log('Score reported successfully:', message);
                setNotification({
                    type: 'success',
                    message: 'Score reported successfully!'
                });
            } else if (status === 'error') {
                console.error('Error reporting score:', message);
                setNotification({
                    type: 'error',
                    message: `Failed to report score: ${message}`
                });
            }
        };

        return () => {
            delete window.showReportStatus;
        };
    }, []);

    // Set up debug function to inspect SCORM data in the wrapper iframe
    useEffect(() => {
        window.debugSCORM = () => {
            const wrapper = iframeRef.current;
            if (wrapper && wrapper.contentWindow) {
                try {
                    const wrapperWindow = wrapper.contentWindow;
                    console.log('=== SCORM Debug Info ===');
                    console.log('Wrapper has API:', !!wrapperWindow.API);
                    console.log('Wrapper has API_1484_11:', !!wrapperWindow.API_1484_11);
                    console.log('Wrapper UniversalCore:', wrapperWindow.UniversalCore);
                    if (wrapperWindow.UniversalCore) {
                        console.log('SCORM Data:', wrapperWindow.UniversalCore.data);
                        console.log('SCORM Session:', wrapperWindow.UniversalCore.session);
                        console.log('Has Reported:', wrapperWindow.UniversalCore.hasReported);
                    }
                } catch (e) {
                    console.error('Cannot access wrapper window (cross-origin):', e);
                }
            } else {
                console.error('Wrapper iframe not available');
            }
        };
        console.log('💡 Debug tip: Type debugSCORM() in console to inspect SCORM data');
    }, [courseUrl]);

    if (!email) {
        return null;
    }

    if (isLoading) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh">
                    <CircularProgress />
                    <Typography variant="h6" mt={2}>
                        Loading SCORM course...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (error) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                <Box display="flex" alignItems="center" justifyContent="center" height="80vh" p={3}>
                    <Alert severity="error" sx={{ maxWidth: 600 }}>
                        <Typography variant="h6">Error Loading Course</Typography>
                        <Typography>{error}</Typography>
                    </Alert>
                </Box>
            </MainCard>
        );
    }

    return (
        <>
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                {/* Only load iframe after API is ready */}
                {apiReady && courseUrl ? (
                    <iframe
                        ref={iframeRef}
                        style={{ width: "100%", height: "100vh", border: "none" }}
                        title={`scorm-course-${courseId}`}
                        src={courseUrl}
                        allow="autoplay; fullscreen"
                        allowFullScreen
                    />
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh">
                        <CircularProgress />
                        <Typography variant="h6" mt={2}>
                            Initializing SCORM runtime...
                        </Typography>
                    </Box>
                )}
            </MainCard>

            {/* Notification Snackbar */}
            {notification && (
                <Snackbar
                    open={!!notification}
                    autoHideDuration={6000}
                    onClose={() => setNotification(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setNotification(null)}
                        severity={notification.type}
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            )}
        </>
    );
}